// Shared helpers for the Documents module (policies & company documents).
import { BookOpen, FileText } from "lucide-react";

export const DOCS_ACCENT = "#6b4f8a";
export const ROOT = "__root__";

export const LIBRARY_META = {
  policies: {
    label: "Policies & Procedures", singular: "policy", icon: BookOpen,
    desc: "The organisation's current policies and procedures. Organise them into folders by category.",
  },
  company: {
    label: "Company Documents", singular: "document", icon: FileText,
    desc: "How-to guides, best practice guides and other company documents.",
  },
};

// { id -> folder }, { parentId|ROOT -> [children sorted by name] }
export function indexFolders(folders) {
  const byId = {}, kids = {};
  (folders || []).forEach((f) => { byId[f.id] = f; });
  (folders || []).forEach((f) => { (kids[f.parent_id || ROOT] = kids[f.parent_id || ROOT] || []).push(f); });
  Object.values(kids).forEach((l) => l.sort((a, b) => a.name.localeCompare(b.name)));
  return { byId, kids };
}

export function folderPath(byId, id) {
  const out = [];
  const seen = new Set();
  while (id && byId[id] && !seen.has(id)) { seen.add(id); out.unshift(byId[id]); id = byId[id].parent_id; }
  return out;
}

// [{ id, label: "HR / Leave" }] sorted by label, optionally excluding some ids.
export function folderOptions(folders, exclude = new Set()) {
  const { byId } = indexFolders(folders);
  return (folders || []).filter((f) => !exclude.has(f.id))
    .map((f) => ({ id: f.id, label: folderPath(byId, f.id).map((p) => p.name).join(" / ") }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const docLabel = (d) => [d?.code, d?.title].filter(Boolean).join(" ");

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Business date "YYYY-MM-DD" -> "15 Oct 2027" (matches fmtLogDay for system timestamps).
export function fmtDay(iso) {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  return y && m && d ? `${d} ${MONTHS[m - 1]} ${y}` : "—";
}

// Version labels ("1.3"), mirroring backend gov_docs.next_version.
export function nextVersion(latest, bump = "minor", first = null) {
  const m = String(latest || "").match(/^(\d+)(?:\.(\d+))?/);
  if (!m) return first || "1.0";
  const major = Number(m[1]), minor = Number(m[2] || 0);
  return bump === "major" ? `${major + 1}.0` : `${major}.${minor + 1}`;
}

// Suggested code prefix from a folder name: "01 - Governance and Management" -> "GOV".
export function suggestPrefix(name) {
  const word = String(name || "").replace(/^[\s\d.\-–_)]+/, "").split(/[\s&/,-]+/).find((w) => /[a-z]/i.test(w)) || "";
  return word.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase();
}
